import React, { useState, useEffect } from 'react';
import { Routes, Route, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import './Dashboard.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const API_URL = process.env.REACT_APP_API_URL || '';

// Overview Component
const Overview = () => {
  const { token } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [linkCopied, setLinkCopied] = useState(false);
  const [conversions, setConversions] = useState([]);
  const [conversionsLoading, setConversionsLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
    fetchConversions();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/affiliate/dashboard`, {
        headers: { 'x-session-token': token }
      });
      setDashboardData(response.data);
    } catch (err) {
      console.error('Failed to fetch dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchConversions = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/affiliate/conversions?limit=20`, {
        headers: { 'x-session-token': token }
      });
      setConversions(response.data.conversions || []);
    } catch (err) {
      console.error('Failed to fetch conversions:', err);
    } finally {
      setConversionsLoading(false);
    }
  };

  const copyAffiliateLink = () => {
    navigator.clipboard.writeText(dashboardData?.affiliate?.affiliate_link);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  if (loading) {
    return <div className="loading-container"><div className="loading-spinner" /></div>;
  }

  const stats = dashboardData?.stats || {};
  const conversionRate = stats.totalClicks > 0 
    ? ((stats.totalConversions / stats.totalClicks) * 100).toFixed(1) 
    : '0.0';

  const chartData = {
    labels: dashboardData?.chartData?.map(d => {
      const date = new Date(d.date);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }) || [],
    datasets: [
      {
        label: 'Clicks',
        data: dashboardData?.chartData?.map(d => d.clicks) || [],
        borderColor: 'rgba(99, 102, 241, 1)',
        backgroundColor: 'rgba(99, 102, 241, 0.1)',
        fill: true,
        tension: 0.4,
        pointRadius: 3,
        pointBackgroundColor: 'rgba(99, 102, 241, 1)',
        pointHoverRadius: 5,
        pointHoverBackgroundColor: 'rgba(99, 102, 241, 1)',
      },
      {
        label: 'Conversions',
        data: dashboardData?.chartData?.map(d => d.conversions) || [],
        borderColor: 'rgba(201, 169, 98, 1)',
        backgroundColor: 'rgba(201, 169, 98, 0.15)',
        fill: true,
        tension: 0.4,
        pointRadius: 3,
        pointBackgroundColor: 'rgba(201, 169, 98, 1)',
        pointHoverRadius: 5,
        pointHoverBackgroundColor: 'rgba(201, 169, 98, 1)',
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        align: 'end',
        labels: {
          color: '#888',
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 20,
          font: { size: 12 }
        }
      },
      tooltip: {
        backgroundColor: '#18181B',
        titleColor: '#E4E4E7',
        bodyColor: '#A1A1AA',
        borderColor: '#27272C',
        borderWidth: 1,
        padding: 12
      }
    },
    scales: {
      x: {
        grid: { color: 'rgba(39, 39, 44, 0.5)' },
        ticks: { color: '#71717A', maxTicksLimit: 10 }
      },
      y: {
        grid: { color: 'rgba(39, 39, 44, 0.5)' },
        ticks: { color: '#71717A', stepSize: 1 },
        beginAtZero: true
      }
    },
    interaction: {
      intersect: false,
      mode: 'index'
    }
  };

  return (
    <div className="overview">
      {/* Affiliate Link Section */}
      <div className="affiliate-link-section animate-fade-in">
        <h3>Your Affiliate Link</h3>
        <div className="affiliate-link-box">
          <code>{dashboardData?.affiliate?.affiliate_link}</code>
          <button className="btn btn-primary" onClick={copyAffiliateLink}>
            {linkCopied ? '✓ Copied!' : 'Copy Link'}
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid animate-fade-in">
        <div className="stats-card stats-card-clicks">
          <div className="stats-icon">👆</div>
          <div className="stats-content">
            <span className="stats-title">Total Clicks</span>
            <span className="stats-value stats-value-clicks">{stats.totalClicks?.toLocaleString() || '0'}</span>
            <span className="stats-subtitle">Link clicks tracked</span>
          </div>
        </div>

        <div className="stats-card stats-card-conversions">
          <div className="stats-icon">🎯</div>
          <div className="stats-content">
            <span className="stats-title">Conversions</span>
            <span className="stats-value stats-value-gold">{stats.totalConversions?.toLocaleString() || '0'}</span>
            <span className="stats-subtitle">Successful orders</span>
          </div>
        </div>

        <div className="stats-card stats-card-rate">
          <div className="stats-icon">📊</div>
          <div className="stats-content">
            <span className="stats-title">Conversion Rate</span>
            <span className="stats-value stats-value-rate">{conversionRate}%</span>
            <span className="stats-subtitle">Clicks → Orders</span>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="chart-card animate-fade-in">
        <h3>Performance (Last 30 Days)</h3>
        <div className="chart-container">
          <Line data={chartData} options={chartOptions} />
        </div>
      </div>

      {/* Recent Conversions */}
      <div className="table-card animate-fade-in">
        <h3>Recent Conversions</h3>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Customer</th>
                <th>Order ID</th>
                <th>Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {conversionsLoading && (
                <tr>
                  <td colSpan="5" className="empty-state">Loading...</td>
                </tr>
              )}
              {!conversionsLoading && conversions.map((conv) => (
                <tr key={conv.id}>
                  <td>{new Date(conv.created_at).toLocaleDateString()}</td>
                  <td>{conv.customer_email}</td>
                  <td><code>{conv.order_id}</code></td>
                  <td className="amount-cell">€{parseFloat(conv.order_total).toFixed(2)}</td>
                  <td>
                    <span className={`status-badge status-${conv.status}`}>
                      {conv.status}
                    </span>
                  </td>
                </tr>
              ))}
              {!conversionsLoading && conversions.length === 0 && (
                <tr>
                  <td colSpan="5" className="empty-state">No conversions yet</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Admin Overview Component
const AdminOverview = () => {
  const { token } = useAuth();
  const [affiliates, setAffiliates] = useState([]);
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  const [affiliateDetails, setAffiliateDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    fetchAffiliates();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const affiliateLink = (code) => `https://zlatarenta.com?ref=${code}`;

  const copyLink = (e, affiliate) => {
    e.stopPropagation(); // don't open the details modal when copying
    navigator.clipboard.writeText(affiliateLink(affiliate.affiliate_code));
    setCopiedId(affiliate.id);
    setTimeout(() => setCopiedId((id) => (id === affiliate.id ? null : id)), 2000);
  };

  const fetchAffiliates = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/affiliates`, {
        headers: { 'x-access-token': token }
      });
      setAffiliates(response.data.affiliates || []);
    } catch (err) {
      console.error('Failed to fetch affiliates:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAffiliateDetails = async (affiliate) => {
    setSelectedAffiliate(affiliate);
    setDetailsLoading(true);
    try {
      // Fetch clicks and conversions for this affiliate
      const [clicksRes, conversionsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/affiliates/${affiliate.id}/clicks`, {
          headers: { 'x-access-token': token }
        }),
        axios.get(`${API_URL}/api/admin/conversions?affiliate_id=${affiliate.id}`, {
          headers: { 'x-access-token': token }
        })
      ]);
      
      setAffiliateDetails({
        clicks: clicksRes.data.clicks || [],
        conversions: conversionsRes.data.conversions || [],
        totalClicks: clicksRes.data.total || 0
      });
    } catch (err) {
      console.error('Failed to fetch affiliate details:', err);
      setAffiliateDetails({ clicks: [], conversions: [], totalClicks: 0 });
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeDetails = () => {
    setSelectedAffiliate(null);
    setAffiliateDetails(null);
  };

  if (loading) {
    return <div className="loading-container"><div className="loading-spinner" /></div>;
  }

  // Calculate totals
  const totalClicks = affiliates.reduce((sum, a) => sum + (a.total_clicks || 0), 0);
  const totalConversions = affiliates.reduce((sum, a) => sum + (a.total_referrals || 0), 0);
  const totalAmount = affiliates.reduce((sum, a) => sum + parseFloat(a.total_referral_amount || 0), 0);

  return (
    <div className="overview admin-overview">
      {/* Summary Stats */}
      <div className="stats-grid animate-fade-in">
        <div className="stats-card">
          <div className="stats-icon">👥</div>
          <div className="stats-content">
            <span className="stats-title">Affiliates</span>
            <span className="stats-value">{affiliates.length}</span>
          </div>
        </div>
        <div className="stats-card stats-card-clicks">
          <div className="stats-icon">👆</div>
          <div className="stats-content">
            <span className="stats-title">Total Clicks</span>
            <span className="stats-value stats-value-clicks">{totalClicks.toLocaleString()}</span>
          </div>
        </div>
        <div className="stats-card stats-card-conversions">
          <div className="stats-icon">🎯</div>
          <div className="stats-content">
            <span className="stats-title">Conversions</span>
            <span className="stats-value stats-value-gold">{totalConversions.toLocaleString()}</span>
          </div>
        </div>
        <div className="stats-card stats-card-amount">
          <div className="stats-icon">💰</div>
          <div className="stats-content">
            <span className="stats-title">Total Sales</span>
            <span className="stats-value stats-value-amount">€{totalAmount.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Affiliates List */}
      <div className="table-card animate-fade-in">
        <h3>All Affiliates</h3>
        <div className="table-container">
          <table className="data-table affiliates-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Code</th>
                <th>Link</th>
                <th>Clicks</th>
                <th>Conversions</th>
                <th>Total Sales</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {affiliates.map((affiliate) => (
                <tr 
                  key={affiliate.id} 
                  className="clickable-row"
                  onClick={() => fetchAffiliateDetails(affiliate)}
                >
                  <td>
                    <div className="affiliate-name-cell">
                      <span className="affiliate-name">{affiliate.name}</span>
                      <span className="affiliate-email">{affiliate.email}</span>
                    </div>
                  </td>
                  <td><code className="affiliate-code">{affiliate.affiliate_code}</code></td>
                  <td className="link-cell">
                    <button
                      className="btn btn-ghost copy-link-btn"
                      onClick={(e) => copyLink(e, affiliate)}
                      title={affiliateLink(affiliate.affiliate_code)}
                    >
                      {copiedId === affiliate.id ? '✓ Copied' : '🔗 Copy link'}
                    </button>
                  </td>
                  <td className="clicks-cell">{(affiliate.total_clicks || 0).toLocaleString()}</td>
                  <td className="conversions-cell">{(affiliate.total_referrals || 0).toLocaleString()}</td>
                  <td className="amount-cell">€{parseFloat(affiliate.total_referral_amount || 0).toFixed(2)}</td>
                  <td>
                    <span className={`status-badge status-${affiliate.status}`}>
                      {affiliate.status}
                    </span>
                  </td>
                </tr>
              ))}
              {affiliates.length === 0 && (
                <tr>
                  <td colSpan="7" className="empty-state">No affiliates yet</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Affiliate Details Modal */}
      {selectedAffiliate && (
        <div className="modal-overlay" onClick={closeDetails}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2>{selectedAffiliate.name}</h2>
                <span className="modal-subtitle">{selectedAffiliate.email} • <code>{selectedAffiliate.affiliate_code}</code></span>
              </div>
              <button className="modal-close" onClick={closeDetails}>×</button>
            </div>

            {detailsLoading ? (
              <div className="loading-container"><div className="loading-spinner" /></div>
            ) : (
              <div className="modal-body">
                {/* Affiliate Stats */}
                <div className="stats-grid modal-stats">
                  <div className="stats-card stats-card-clicks">
                    <div className="stats-content">
                      <span className="stats-title">Clicks</span>
                      <span className="stats-value stats-value-clicks">{affiliateDetails?.totalClicks || selectedAffiliate.total_clicks || 0}</span>
                    </div>
                  </div>
                  <div className="stats-card stats-card-conversions">
                    <div className="stats-content">
                      <span className="stats-title">Conversions</span>
                      <span className="stats-value stats-value-gold">{affiliateDetails?.conversions?.length || 0}</span>
                    </div>
                  </div>
                  <div className="stats-card stats-card-amount">
                    <div className="stats-content">
                      <span className="stats-title">Total Sales</span>
                      <span className="stats-value stats-value-amount">
                        €{affiliateDetails?.conversions?.reduce((sum, c) => sum + parseFloat(c.order_total || 0), 0).toFixed(2) || '0.00'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Recent Clicks */}
                <div className="detail-section">
                  <h4>Recent Clicks ({affiliateDetails?.clicks?.length || 0})</h4>
                  <div className="table-container compact">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Source</th>
                          <th>IP Address</th>
                        </tr>
                      </thead>
                      <tbody>
                        {affiliateDetails?.clicks?.slice(0, 10).map((click) => (
                          <tr key={click.id}>
                            <td>{new Date(click.created_at).toLocaleString()}</td>
                            <td>{click.referrer ? new URL(click.referrer).hostname : '-'}</td>
                            <td><code>{click.ip_address || '-'}</code></td>
                          </tr>
                        ))}
                        {(!affiliateDetails?.clicks || affiliateDetails.clicks.length === 0) && (
                          <tr>
                            <td colSpan="3" className="empty-state">No clicks recorded</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Conversions */}
                <div className="detail-section">
                  <h4>Conversions ({affiliateDetails?.conversions?.length || 0})</h4>
                  <div className="table-container compact">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Customer</th>
                          <th>Order ID</th>
                          <th>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {affiliateDetails?.conversions?.map((conv) => (
                          <tr key={conv.id}>
                            <td>{new Date(conv.created_at).toLocaleDateString()}</td>
                            <td>{conv.customer_email}</td>
                            <td><code>{conv.order_id}</code></td>
                            <td className="amount-cell">€{parseFloat(conv.order_total).toFixed(2)}</td>
                          </tr>
                        ))}
                        {(!affiliateDetails?.conversions || affiliateDetails.conversions.length === 0) && (
                          <tr>
                            <td colSpan="4" className="empty-state">No conversions yet</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Main Dashboard Component
const Dashboard = () => {
  const { user, isAdmin, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="dashboard-layout">
      {/* Sidebar */}
      <aside className="sidebar">
        <div className="sidebar-header">
          <h1>ZLATARENTA</h1>
          <span>{isAdmin ? 'Admin Portal' : 'Affiliate Portal'}</span>
        </div>

        <nav className="sidebar-nav">
          <NavLink to="/dashboard" end className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
            <span className="nav-icon">📊</span>
            Overview
          </NavLink>
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <span className="user-name">{user?.name || user?.email}</span>
            <span className="user-email">{isAdmin ? '👑 Admin' : user?.email}</span>
          </div>
          <button className="btn btn-ghost" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        <Routes>
          <Route path="/" element={isAdmin ? <AdminOverview /> : <Overview />} />
        </Routes>
      </main>
    </div>
  );
};

export default Dashboard;

